/**
 * Cats and dogs on the streets (content/animals) and the karma they bring (docs/DESIGN_KARMA.md).
 * An animal is a Person with role 'animal' (it uses the walking physics and the wire), a small
 * body drawn from its content colours. It wanders near its home, sits, runs from cars and
 * gunfire, comes to whoever stands still with food, and a fed dog follows its feeder a while.
 * Pet it (F, on foot, close) or feed it (a food item from the pockets) for karma; hurt it and
 * karma drops — and the neighbourhood notices.
 */
import type { Animal } from '../content/types';
import { count as countAch } from './achievements';
import type { ZoneName } from '../content/types';
import type { Rng } from '../world/rng';
import { doorstep } from '../world/parking';
import { makePerson, type Person } from './entities';
import { moveCircle, tileBlocked } from './physics';
import type { Game, PlayerState } from './game';
import type { ShopRow } from './shop';
import type { Chunk } from '../world/generator';
import type { Registry } from '../content/registry';

export interface AnimalState { id: string; kind: 'cat' | 'dog'; home: [number, number]; think: number; idle: number; dx: number; dy: number; flee: number; /** the player it follows (id) and for how long */ follow?: number; followFor: number; /** game time it was last petted (once per few minutes counts) */ petAt?: number; fedAt?: number }

// (owner, Sep 9: "for running on animal shows -6 now. Its way too high. -1 for it") — a cat under the wheels at a
// junction is an accident; taking a weapon to one is not, and the two should not cost the same.
/**
 * (owner, Sep 9: "while driving, negative karma adds too fast") — the ledger levelled off. A death is two points
 * whether it was a person or an animal; hurting either is one; a car is one whatever it hit. Nothing anyone can do
 * with a steering wheel costs more than two, and the same harm always costs the same, which is what "innocent" means.
 */
export const KARMA = { pet: 1, feed: 2, killAnimal: -2, hurtAnimal: -1, hitAnimal: -1, killCivilian: -2, hurtCivilian: -1, /** a tip's karma: its share of everything you have times `tipPer`, but never less than `tipMin` nor more than `tipCap` — and once per counter (TIP_EVERY) */ tipPer: 50, tipMin: 1, tipCap: 2, robbery: -2, ambulance: 3, fire: 3, taxi: 1, extortionCleared: 2, carjack: -1, min: -100, max: 100 } as const;
const FEED_REACH = 2.2, PET_EVERY = 240; // game seconds between karma-worthy pets of the same animal
/** game seconds before the same counter's tip is worth karma again — the petting rule, for the same reason (owner, Sep 10: a shop should be worth a visit, not worth twenty items) */
export const TIP_EVERY = 240;
export const ANIMALS_PER_ZONE: Record<ZoneName, number> = { water: 0, rural: 2, suburb: 3, industrial: 1, downtown: 2, park: 3 };

export function isAnimal(p: Person): boolean { return p.role === 'animal'; }
export type AnimalHost = { people: Person[]; reg: Registry };
export function animalDef(game: AnimalHost, p: Person): Animal | undefined { return p.animal ? game.reg.get('animal', p.animal.id) : undefined; }

/**
 * How karma is written down, everywhere it is shown: to one decimal, and a whole number when it is whole. Tipping is
 * the only fractional source, and (0.125 karma) on a shop line read like a rounding error rather than a good deed
 * (owner, Sep 10: "too many decimals").
 */
export const karmaText = (k: number): string => (Math.abs(k - Math.round(k)) < 0.05 ? String(Math.round(k)) : k.toFixed(1));
/** the same, with a sign in front — for a change, rather than a total */
export const karmaDelta = (k: number): string => `${k > 0 ? '+' : ''}${karmaText(k)}`;

/** Karma in [-100, 100] on the player; a line when it moves. Kept to one decimal so it never accumulates float dust. */
export function karma(game: Game, st: PlayerState, delta: number, why: string): void {
  const before = st.stats.karma ?? 0;
  st.stats.karma = Math.round(Math.max(KARMA.min, Math.min(KARMA.max, before + delta)) * 10) / 10;
  if (st.stats.karma !== before) game.say(`${karmaDelta(st.stats.karma - before)} karma — ${why}`, st.person);
}

export function makeAnimal(def: Animal, x: number, y: number): Person {
  const q = makePerson(x, y);
  q.role = 'animal'; q.r = def.size; q.hp = def.kind === 'cat' ? 20 : 35; q.maxHp = q.hp; q.weapons = []; q.walker = undefined;
  q.animal = { id: def.id, kind: def.kind, home: [x, y], think: 0, idle: 1, dx: 0, dy: 0, flee: 0, followFor: 0 };
  return q;
}

/** A chunk streams in: a few animals by the houses (cats at doorsteps, dogs in yards) and in the parks. */
export function spawnAnimals(game: Game, c: Chunk, zone: ZoneName, rng: Rng): void {
  const n = ANIMALS_PER_ZONE[zone] ?? 0; if (!n) return;
  const defs = game.reg.forZone('animal', zone); if (!defs.length) return;
  const homes = c.buildings.filter((b) => b.height > 0 && (b.def.tags.includes('house') || b.def.tags.includes('residential') || b.def.tags.includes('park') || b.def.tags.includes('farm')));
  for (let k = 0, made = 0; k < n * 6 && made < n; k++) {
    const def = rng.weighted(defs, (d) => (d.zones[zone] ?? 0) * (d.rarity ?? 1)); if (!def) break;
    let x: number, y: number;
    if (homes.length && rng.chance(0.8)) { const b = rng.pick(homes); const d = doorstep(b, game.seed); const a = rng.float() * Math.PI * 2; x = d.x + Math.cos(a) * (1 + rng.float() * 3); y = d.y + Math.sin(a) * (1 + rng.float() * 3); }
    else { x = c.x0 + rng.int(2, 29) + 0.5; y = c.y0 + rng.int(2, 29) + 0.5; }
    if (!game.world.loaded(x, y) || game.world.buildingAt(x, y) || tileBlocked(game.world, x, y, 0, 'walk') || (game.world.roadAt(x, y) && !(game.world.roadFlags(x, y) & 16))) continue;
    if (game.people.some((q) => q.animal && Math.hypot(q.x - x, q.y - y) < 3)) continue;
    const q = makeAnimal(def, x, y); q.home = `${c.cx},${c.cy}`;
    game.people.push(q); made++;
  }
}

/** An animal's own voice: a pitch from its id (a small dog yaps, a big one woofs; kittens and toms). */
export function voiceOf(q: Person): number { const h = ((q.id * 2654435761) >>> 0) % 1000 / 1000; return q.animal?.kind === 'dog' ? 0.65 + h * 0.75 : 0.85 + h * 0.45; }
/** Every step: wander, sit, flee, come for food, follow the feeder. */
export function stepAnimals(game: Game, dt: number): void {
  const w = game.world, rng = game.npcRng;
  for (const q of game.people) {
    const a = q.animal; if (!a || q.dead) { if (q.animal && q.dead) q.deadFor += dt; continue; }
    const def = game.reg.get('animal', a.id); const speed = def?.speed ?? 3;
    // danger: a car coming within 5 m, or gunfire nearby (the game's scare() sets flee on walkers; animals check cars themselves)
    const scare = a.follow !== undefined ? 3 : 6; // a dog at heel only jumps aside from a car right on top of it
    if (a.flee <= 0) { for (const v of game.vehicles) { if (Math.abs(v.speed) < 2 || v.alt > 0.5) continue; const dx = q.x - v.x, dy = q.y - v.y, d = Math.hypot(dx, dy); if (d < scare) { a.flee = a.follow !== undefined ? 0.8 : 3; a.dx = dx / (d || 1); a.dy = dy / (d || 1); if (rng.chance(0.5)) game.events.push({ kind: 'animal', x: q.x, y: q.y, cls: a.kind, pitch: voiceOf(q) }); break; } } }
    if (a.flee > 0) {
      a.flee -= dt; q.angle = Math.atan2(a.dx, -a.dy);
      if (moveCircle(w, q, a.dx * speed * 1.4 * dt, a.dy * speed * 1.4 * dt, q.r, 0, 'walk')) { const t = rng.float() * Math.PI * 2; a.dx = Math.cos(t); a.dy = Math.sin(t); }
      continue;
    }
    // somebody holding food nearby (standing still, on foot): come over; a fed dog keeps following its feeder.
    // Karma (Q55): a good name draws strays over even empty-handed; a bad one makes them slink off when you come near.
    const kAt = game.reg.economy.karma.animalAt;
    if (a.follow === undefined) {
      for (const st of game.players.values()) { const p = st.person; if (p.dead || p.vehicle || p.inside) continue; const d = Math.hypot(p.x - q.x, p.y - q.y); const k = st.stats.karma ?? 0;
        if (k <= -kAt && d < 4 && d > 0.01) { a.flee = 2; a.dx = (q.x - p.x) / d; a.dy = (q.y - p.y) / d; if (rng.chance(0.3)) game.events.push({ kind: 'animal', x: q.x, y: q.y, cls: a.kind, pitch: voiceOf(q) }); break; }
        if (Math.hypot(st.input.moveX, st.input.moveY) > 0 || !(hasFood(game, p, a.kind) || k >= kAt)) continue;
        if (d < 12 && d > FEED_REACH * 0.7) { a.follow = p.id; a.followFor = 6; break; } }
      if (a.flee > 0) continue;
    }
    if (a.follow !== undefined) {
      a.followFor -= dt; if (a.followFor <= 0) { a.follow = undefined; a.idle = 1; continue; }
      const p = game.people.find((x) => x.id === a.follow); if (!p || p.dead || p.vehicle || p.inside) { a.follow = undefined; continue; }
      const dx = p.x - q.x, dy = p.y - q.y, d = Math.hypot(dx, dy);
      if (d > 1.6) { q.angle = Math.atan2(dx, -dy); moveCircle(w, q, (dx / d) * speed * dt, (dy / d) * speed * dt, q.r, 0, 'walk'); }
      if (a.kind === 'dog' && rng.chance(dt * 0.08)) game.events.push({ kind: 'animal', x: q.x, y: q.y, cls: 'dog', pitch: voiceOf(q) }); // a bark now and then
      continue;
    }
    if (a.idle > 0) { a.idle -= dt; continue; }
    a.think -= dt;
    if (a.think <= 0) {
      a.think = 1 + rng.float() * 3;
      if (rng.chance(a.kind === 'cat' ? 0.5 : 0.3)) { a.idle = 2 + rng.float() * 6; continue; } // cats sit a lot
      // a new heading, drifting back home when far
      const hx = a.home[0] - q.x, hy = a.home[1] - q.y, hd = Math.hypot(hx, hy);
      const t = hd > 14 ? Math.atan2(hy, hx) + (rng.float() - 0.5) : rng.float() * Math.PI * 2;
      a.dx = Math.cos(t); a.dy = Math.sin(t);
    }
    q.angle = Math.atan2(a.dx, -a.dy);
    const sp = speed * (a.kind === 'cat' ? 0.45 : 0.55);
    if (moveCircle(w, q, a.dx * sp * dt, a.dy * sp * dt, q.r, 0, 'walk', (x, y) => !(w.roadAt(x, y) && !(w.roadFlags(x, y) & 16)))) a.think = 0; // stays off the carriageway
  }
}

/** items in the pockets this kind of animal will eat (`feeds` on the item — pet food, fish, ham, milk for cats, water); chocolate stays in the pocket */
function feedable(game: AnimalHost, p: Person, kind: 'cat' | 'dog'): { id: string; v: number }[] {
  const out: { id: string; v: number }[] = [];
  for (const [id, n] of Object.entries(p.items)) { const it = game.reg.get('item', id); if (!it || n <= 0 || !it.feeds?.includes(kind)) continue; out.push({ id, v: it.value }); }
  return out.sort((a, b) => a.v - b.v); // the cheapest first: a tin before the fish dinner
}
function hasFood(game: AnimalHost, p: Person, kind: 'cat' | 'dog' = 'dog'): boolean { return feedable(game, p, kind).length > 0; }
function foodInPockets(game: AnimalHost, p: Person, kind: 'cat' | 'dog'): string | undefined { return feedable(game, p, kind)[0]?.id; }

/** The nearest animal within reach on foot, outdoors. */
export function animalNear(game: AnimalHost, p: Person): Person | undefined {
  if (p.vehicle || p.inside || p.dead) return undefined;
  let best: Person | undefined, bd = FEED_REACH;
  for (const q of game.people) { if (!q.animal || q.dead) continue; const d = Math.hypot(q.x - p.x, q.y - p.y); if (d < bd) { bd = d; best = q; } }
  return best;
}
/** F list rows beside an animal: pet, and feed when you carry food. */
export function animalRows(game: AnimalHost, p: Person): { name: string; rows: ShopRow[] } | undefined {
  const q = animalNear(game, p); if (!q) return undefined;
  const def = animalDef(game, q); const name = def?.name ?? (q.animal!.kind === 'cat' ? 'Cat' : 'Dog');
  const rows: ShopRow[] = [{ kind: 'animal', id: `pet:${q.id}`, name: `Pet the ${name.toLowerCase()}`, price: 0 }];
  const food = foodInPockets(game, p, q.animal!.kind);
  if (food) rows.push({ kind: 'animal', id: `feed:${q.id}:${food}`, name: `Feed it (${game.reg.get('item', food)?.name ?? food})`, price: 0 });
  return { name, rows };
}
export function pickAnimal(game: Game, st: PlayerState, row: ShopRow): void {
  const p = st.person; const [what, idS, food] = (row.id ?? '').split(':'); const q = game.people.find((x) => x.id === Number(idS) && x.animal && !x.dead); if (!q) return;
  const a = q.animal!, def = animalDef(game, q), name = (def?.name ?? 'animal').toLowerCase();
  if (Math.hypot(q.x - p.x, q.y - p.y) > FEED_REACH + 0.5) { game.say('Too far', p); return; }
  if (what === 'pet') {
    const fresh = a.petAt === undefined || game.time - a.petAt > PET_EVERY;
    a.petAt = game.time; a.idle = 3; a.flee = 0; q.angle = Math.atan2(p.x - q.x, -(p.y - q.y));
    if (fresh) countAch(game, st, 'animals_petted', 1); // (owner, Sep 28)
    if (fresh) karma(game, st, KARMA.pet, `you petted the ${name}`); else game.say(`The ${name} purrs${a.kind === 'dog' ? ' — well, wags' : ''}`, p);
    game.events.push({ kind: 'animal', x: q.x, y: q.y, cls: a.kind, pitch: voiceOf(q) });
    return;
  }
  if (what === 'feed' && food) {
    const n = p.items[food] ?? 0; if (n <= 0) { game.say('Nothing to feed it', p); return; }
    p.items[food] = n - 1; if (p.items[food]! <= 0) delete p.items[food];
    a.fedAt = game.time; a.idle = 2; a.flee = 0;
    if (a.kind === 'dog') { a.follow = p.id; a.followFor = 120; }
    countAch(game, st, 'animals_fed', 1); // (owner, Sep 28)
    karma(game, st, KARMA.feed, `you fed the ${name}${a.kind === 'dog' ? ' — it will follow you a while' : ''}`);
    game.events.push({ kind: 'animal', x: q.x, y: q.y, cls: a.kind, pitch: voiceOf(q) });
  }
}

/**
 * Hurting an animal: karma down; killing it more, and the neighbours run. Under a car it is a single point either way —
 * you did not mean to, and the game should not treat a junction like a crime scene (owner, Sep 9).
 */
export function animalHurt(game: Game, q: Person, by: PlayerState | undefined, killed: boolean, how?: 'vehicle'): void {
  if (!by) return;
  const name = (animalDef(game, q)?.name ?? 'animal').toLowerCase();
  if (how === 'vehicle') { karma(game, by, KARMA.hitAnimal, killed ? `you ran over a ${name}` : `you clipped a ${name}`); return; }
  karma(game, by, killed ? KARMA.killAnimal : KARMA.hurtAnimal, killed ? `you killed a ${name}` : `you hurt a ${name}`);
}

/** How many animals live in a chunk — for tests and the report. */
export function animalsIn(game: Game, cx: number, cy: number): number { const key = `${cx},${cy}`; return game.people.filter((q) => q.animal && q.home === key).length; }
